import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Size scale — the same six intents as BrandLoader, so swapping one for the
 * other at a call site never changes the layout:
 *
 *   inline (12) — inside a line of text / a dense status row
 *   glyph  (16) — icon slot: a button's icon, a list-row leading glyph
 *   sm     (24) — inside a control (button body, small tile)
 *   md     (32) — a card or pane's own loading moment
 *   lg     (48) — a page section / modal body
 *   xl     (96) — the largest step; rarely right for a plain wait
 */
export type SpinnerSize = 'inline' | 'glyph' | 'sm' | 'md' | 'lg' | 'xl';

/** Named intent → px. The single source for the scale. */
export const SPINNER_PX: Record<SpinnerSize, number> = {
  inline: 12,
  glyph: 16,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 96,
};

const SIZE_CLASS: Record<SpinnerSize, string> = {
  inline: 'w-3 h-3',
  glyph: 'w-4 h-4',
  sm: 'w-6 h-6',
  md: 'w-8 h-8',
  lg: 'w-12 h-12',
  xl: 'w-24 h-24',
};

/** Arc colour. The track is always the hairline token. */
export type SpinnerTone = 'muted' | 'current' | 'brand';

const TONE_CLASS: Record<SpinnerTone, string> = {
  muted: 'text-fg-muted',
  current: 'text-current',
  brand: 'text-champagne',
};

export interface SpinnerProps {
  size?: SpinnerSize;
  /**
   * `muted` (default) is the quiet neutral wait; `current` inherits `color`
   * from the parent (inside a button, badge or pill); `brand` draws the arc
   * in champagne — for a deliberate accent, not as a substitute for the
   * logo loader.
   */
  tone?: SpinnerTone;
  /** Visible caption under the spinner. Pass an already-translated string. */
  label?: string;
  /** Accessible name when there is no visible `label`. */
  ariaLabel?: string;
  /** Wrap in a flex-centered container */
  centered?: boolean;
  className?: string;
}

// Geometry of the 24-unit viewBox ring. Circumference = 2πr ≈ 56.5, so a
// 14-unit dash is a touch under a quarter turn — enough arc to read as motion
// without becoming a second ring.
const RADIUS = 9;
const STROKE_WIDTH = 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const ARC_LENGTH = 14;

/**
 * Spinner — THE plain, neutral wait indicator.
 *
 * Use it for ordinary waiting: a pending button, an inline status, a small
 * section or a data fetch that has no content shape to skeleton. It is
 * deliberately NOT the PONS mark: `BrandLoader` draws the logo and is
 * reserved for AI processes (generation, extraction, analysis). A list of
 * collaborators loading is not an AI process.
 *
 * Order of preference: `Skeleton` (content areas) → `Spinner` (everything
 * else) → `BrandLoader` (AI only). See `shared/LOADERS.md`.
 *
 *     <Spinner size="glyph" tone="current" />   // inside a button
 *     <Spinner size="md" centered />            // a card's own wait
 *
 * Respects `prefers-reduced-motion`: the rotation stops and the arc settles
 * into a dimmed static ring (see `.pons-spinner` in `index.css`).
 */
const Spinner: React.FC<SpinnerProps> = ({
  size = 'md',
  tone = 'muted',
  label,
  ariaLabel,
  centered = false,
  className = '',
}) => {
  const { t } = useTranslation();

  const spinner = (
    <div
      role="status"
      aria-live="polite"
      aria-label={ariaLabel || label || t('Loading')}
      className="flex flex-col items-center gap-3"
    >
      <svg
        viewBox="0 0 24 24"
        className={`${SIZE_CLASS[size]} ${TONE_CLASS[tone]} ${className}`}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* Track — a hairline, the same weight as every border in the app. */}
        <circle
          cx="12"
          cy="12"
          r={RADIUS}
          fill="none"
          stroke="rgb(var(--hairline))"
          strokeWidth={STROKE_WIDTH}
        />
        {/* Arc — takes its colour from `tone` via currentColor. */}
        <circle
          className="pons-spinner pons-spinner-arc"
          cx="12"
          cy="12"
          r={RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          strokeDasharray={`${ARC_LENGTH} ${CIRCUMFERENCE - ARC_LENGTH}`}
        />
      </svg>
      {label && <span className="text-sm text-fg-muted">{label}</span>}
    </div>
  );

  if (centered) {
    return <div className="flex items-center justify-center">{spinner}</div>;
  }

  return spinner;
};

export default Spinner;
